<template>
  <div>
    <div class="row">
      <div class="col-12 mt-3">
        <Card body-classes="p-3" card-classes="shadow-sm">
          <template #header>
            <div class="d-flex flex-row align-items-center justify-content-between">
              <h5 class="mb-0 text-primary">
                <vue-feather type="pie-chart" class="me-2"></vue-feather>
                General Analytics Summary
              </h5>
              <div class="d-flex align-items-center">
                <label for="yearSelect" class="me-2 text-muted">Select Year:</label>
                <select id="yearSelect" class="form-select form-select-sm w-auto" v-model="year">
                  <option v-for="year in analytics.years" :key="year" :value="year">{{ year }}</option>
                </select>
              </div>
            </div>
          </template>
          <div>
            <div v-if="loading" class="text-center py-5">
              <Spinner spinner-class="mb-1" />
              <p class="text-muted mt-2">Loading analytics data...</p>
            </div>
            <div v-else>
              <!-- Summary Cards -->
              <div class="row g-3 mb-4">
                <div class="col-xl-4 col-lg-6 col-md-6 col-sm-12" v-for="(item, index) in infoCols" :key="index">
                  <div class="card h-100 border-0 shadow-sm">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="rounded-circle p-3 me-3" :class="`bg-${item.bg}-light`">
                          <vue-feather :type="item.icon" :stroke="item.color" size="24"></vue-feather>
                        </div>
                        <div>
                          <h6 class="mb-0">{{ item.title }}</h6>
                          <small class="text-muted">{{ analyticsText(item.desc) }}</small>
                        </div>
                      </div>
                      <div class="mt-3">
                        <h3 :class="`text-${item.bg}`">{{ item.value }}</h3>
                        <div class="progress mt-2" style="height: 5px;">
                          <div class="progress-bar" :class="`bg-${item.bg}`" role="progressbar"
                               :style="{width: getProgressWidth(index)}"
                               aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Charts -->
              <div class="row g-4">
                <div class="col-xl-6 col-lg-12">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-body">
                      <h6 class="text-muted mb-2">Monthly Sales ({{ this.year }})</h6>
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <span class="text-primary fw-bold">
                          Total: {{ formatNumber(this.analytics.sales.total) }}
                        </span>
                        <span class="badge bg-primary-light text-primary">
                          {{ this.analytics.sales.count }} Sales
                        </span>
                      </div>
                      <div>
                        <div v-if="loading" class="text-center py-5">
                          <Spinner spinner-class="mb-1" />
                        </div>
                        <BarChart
                            v-else
                            :data="this.analytics.sales.totals"
                            :labels="this.analytics.sales.months"
                        >
                        </BarChart>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-xl-6 col-lg-12">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-body">
                      <h6 class="text-muted mb-2">Sales Per Store ({{ this.year }})</h6>
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <span class="text-primary fw-bold">
                          Total: {{ formatNumber(this.analytics.sales.total) }}
                        </span>
                        <span class="badge bg-info-light text-info">
                          {{ this.analytics.stores.datasets.length }} Stores
                        </span>
                      </div>
                      <div>
                        <div v-if="loading" class="text-center py-5">
                          <Spinner spinner-class="mb-1" />
                        </div>
                        <LineChart
                            v-else
                            :data="this.analytics.stores.datasets"
                            :labels="this.analytics.stores.labels"
                        ></LineChart>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import {decrypt} from "@/utils/crypto";
import analyticsService from "@/api/analyticsService";
import formatNumber from "@/utils/formatNumber";
import userService from "@/api/userService";
import LineChart from "@/components/charts/LineChart.vue";
import BarChart from "@/components/charts/BarChart.vue";

export default {
  name: "GeneralAnalytics",
  components: {BarChart, LineChart},

  data(){
    return {
      users: [],
      profile: {},
      store: {},
      loading: true,
      analytics: {
        sales: {
          months: [],
          totals: []
        },
        stores: {
          labels: [],
          datasets: []
        },
        products: {}
      },
      year: new Date().getFullYear(),
    }
  },

  methods: {
    formatNumber,
    analyticsText(desc) {
      return desc ? desc : 'In the year ' + this.year
    },
    async getAnalytics() {
      this.loading = true
      this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
      this.users = await userService.getUsers()
      this.analytics = await analyticsService.getAnalytics(this.year, this.store.id)
      this.loading = false
    },

    getProgressWidth(index) {
      // Calculate relative percentages for progress bars
      if (!this.analytics || !this.infoCols || this.infoCols.length === 0) {
        return '0%';
      }

      const values = this.infoCols.map(item => {
        const numValue = parseFloat(item.value.replace(/[^0-9.-]+/g, ''));
        return isNaN(numValue) ? 0 : numValue;
      });

      const maxValue = Math.max(...values);
      if (maxValue === 0) return '0%';

      const currentValue = values[index];
      return `${(currentValue / maxValue * 100).toFixed(0)}%`;
    }
  },

  computed: {
    infoCols() {
      return [
        {
          title: this.store.name + ' Sales (' + this.analytics.sales.count + ')',
          value:formatNumber(this.analytics.sales.total - this.analytics.sales.discount, false),
          icon:"dollar-sign",
          color:"darkviolet",
          bg:'primary',
        },
        {
          title:this.store.name + ' Tax (' + this.analytics.sales.count + ')',
          value:formatNumber(this.analytics.sales.tax, false),
          icon:"pie-chart",
          color:"crimson",
          bg:'secondary',
        },
        {
          title:this.store.name + ' Discounts (' + this.analytics.sales.count + ')',
          value:formatNumber(this.analytics.sales.discount, false),
          icon:"percent",
          color:"dodgerblue",
          bg:'info',
        },
        {
          title:this.store.name + ' Stock Pur. (' + this.analytics.products.count + ')',
          value:formatNumber(this.analytics.products.buy, false),
          icon:"package",
          color:"gold",
          bg:'warning',
          desc:'Purchase value',
        },
        {
          title:this.store.name + ' Stock Sale (' + this.analytics.products.count + ')',
          value:formatNumber(this.analytics.products.sell, false),
          icon:"package",
          color:"gray",
          bg:'default',
          desc:'Sale value',
        },
        {
          title:this.store.name + ' Stock Profit (' + this.analytics.products.count + ')',
          value:formatNumber(this.analytics.products.sell - this.analytics.products.buy, false),
          icon:"trending-up",
          color:"green",
          bg:'success',
          desc: 'Estimated profit'
        },
      ]
    },
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    await this.getAnalytics()
  },

  watch: {
    year: async function () {
      await this.getAnalytics()
    }
  }

}
</script>

<style scoped>

</style>
